import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Users, Search, Calendar, ArrowRight } from 'lucide-react';

import LoadingSpinner from '../components/LoadingSpinner';
import { useData } from '../contexts/DataContext';
import { ProjectWithComponents } from '../types';
import { formatDate } from '../utils/filters';

interface PersonStats {
  name: string;
  role: 'Owner' | 'Maintainer';
  projects: string[];
  totalProjects: number;
  totalComponents: number;
  lastActivity: string;
}

// Extract people from maintainers and owners
const extractPeopleFromProjects = (projects: ProjectWithComponents[]): PersonStats[] => {
  const peopleMap = new Map<string, PersonStats>();

  projects.forEach(project => {
    // Extract owners
    const owners = project.owner || [];
    owners.forEach(ownerName => {
      if (!peopleMap.has(ownerName)) {
        peopleMap.set(ownerName, {
          name: ownerName,
          role: 'Owner',
          projects: [],
          totalProjects: 0,
          totalComponents: 0,
          lastActivity: project.lastUpdated || new Date().toISOString()
        });
      }
      
      const person = peopleMap.get(ownerName)!;
      // Only add project if not already added (in case person is both owner and maintainer)
      if (!person.projects.includes(project.name)) {
        person.projects.push(project.name);
        person.totalProjects++;
        person.totalComponents += project.components?.length || 0;
      }
      
      // Update last activity if this project is more recent
      if (project.lastUpdated && new Date(project.lastUpdated) > new Date(person.lastActivity)) {
        person.lastActivity = project.lastUpdated;
      }
    });

    // Extract maintainers
    const maintainers = project.maintainers || [];
    maintainers.forEach(maintainerName => {
      if (!peopleMap.has(maintainerName)) {
        peopleMap.set(maintainerName, {
          name: maintainerName,
          role: 'Maintainer',
          projects: [],
          totalProjects: 0,
          totalComponents: 0,
          lastActivity: project.lastUpdated || new Date().toISOString()
        });
      }
      
      const person = peopleMap.get(maintainerName)!;
      // Only add project if not already added (in case person is both owner and maintainer)
      if (!person.projects.includes(project.name)) {
        person.projects.push(project.name);
        person.totalProjects++;
        person.totalComponents += project.components?.length || 0;
      }
      
      // Update last activity if this project is more recent
      if (project.lastUpdated && new Date(project.lastUpdated) > new Date(person.lastActivity)) {
        person.lastActivity = project.lastUpdated;
      }
    });
  });
  
  return Array.from(peopleMap.values());
};

const PeoplePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('name-asc');
  const [peopleStats, setPeopleStats] = useState<PersonStats[]>([]);

  // Use DataContext instead of useProjects hook
  const { getAllProjects, isLoading, error } = useData();
  const projects = getAllProjects();
  
  // Extract people from projects when data changes
  useEffect(() => {
    if (projects) {
      // Extract people from maintainers and owners
      const stats = extractPeopleFromProjects(projects);
      setPeopleStats(stats);
    }
  }, [projects]);

  // Filter and sort people
  const filteredAndSortedPeople = peopleStats
    .filter(person => 
      person.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const [field, direction] = sortOption.split('-');
      
      let comparison = 0;
      
      switch (field) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'totalProjects':
          comparison = a.totalProjects - b.totalProjects;
          break;
        case 'totalComponents':
          comparison = a.totalComponents - b.totalComponents;
          break;
        case 'lastActivity':
          comparison = new Date(a.lastActivity).getTime() - new Date(b.lastActivity).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return direction === 'desc' ? -comparison : comparison;
    });

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Error Loading People</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
          <Link to="/" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold gradient-text mb-2">
          PatternLab People
        </h1>
        <p className="text-slate-600 dark:text-slate-300 text-lg">
          Meet the owners and maintainers behind PatternLab projects
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card card-hover liquid-glass text-center p-6">
          <div className="text-3xl font-bold gradient-text mb-2">
            {peopleStats.length}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            Total People
          </div>
        </div>
        <div className="card card-hover liquid-glass text-center p-6">
          <div className="text-3xl font-bold gradient-text mb-2">
            {peopleStats.reduce((sum, p) => sum + p.totalProjects, 0)}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            Total Project Roles
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              People ({filteredAndSortedPeople.length})
            </h2>
          </div>

          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search people..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-glass pl-10 pr-4 w-64"
              />
            </div>

            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="input-glass"
            >
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="totalProjects-desc">Most Projects</option>
              <option value="totalProjects-asc">Least Projects</option>
              <option value="totalComponents-desc">Most Components</option>
              <option value="totalComponents-asc">Least Components</option>
              <option value="lastActivity-desc">Recently Active</option>
              <option value="lastActivity-asc">Least Active</option>
            </select>
          </div>
        </div>
      </div>

      {/* People Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAndSortedPeople.map((person) => (
          <Link
            key={person.name}
            to={`/people/${encodeURIComponent(person.name)}`}
            className="card card-hover liquid-glass group cursor-pointer p-6"
          >
            {/* Person Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <User className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {person.name}
              </h3>
              <div className="flex items-center justify-center">
                <span className={`badge ${
                  person.role === 'Owner' 
                    ? 'badge-blue'
                    : 'badge-green'
                }`}>
                  <Users className="w-3 h-3 mr-1" />
                  {person.role}
                </span>
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 glass-morphism rounded-xl">
                <div className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                  {person.projects.length}
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Projects
                </div>
              </div>
              <div className="text-center p-3 glass-morphism rounded-xl">
                <div className="text-xl font-bold text-green-600 dark:text-green-400 mb-1">
                  {person.totalComponents}
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Components
                </div>
              </div>
            </div>

            {/* Project Preview */}
            <div className="mb-4">
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">Recent Projects</div>
              <div className="space-y-2">
                {person.projects
                  .slice(0, 3)
                  .map((projectName, index) => (
                    <div key={projectName} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 dark:text-slate-300 truncate">
                        {projectName}
                      </span>
                      <span className="text-xs px-2 py-1 rounded bg-blue-100/80 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                        {person.role}
                      </span>
                    </div>
                  ))}
                {person.projects.length > 3 && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 text-center">
                    +{person.projects.length - 3} more projects
                  </div>
                )}
              </div>
            </div>

            {/* Last Activity */}
            <div className="flex items-center justify-center text-sm text-slate-500 dark:text-slate-400 mb-4">
              <Calendar className="w-4 h-4 mr-2" />
              <span>Active {formatDate(person.lastActivity)}</span>
            </div>

            {/* Action Button */}
            <div className="flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
              <span className="text-sm font-medium mr-2">View Profile</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>

      {filteredAndSortedPeople.length === 0 && (
        <div className="text-center py-12">
          <Search className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            No people found
          </h3>
          <p className="text-slate-600 dark:text-slate-300">
            Try adjusting your search terms
          </p>
        </div>
      )}
    </div>
  );
};

export default PeoplePage;
